import React from 'react';
import { Button, Input, Space, TableProps, message, Popover, Modal } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getTextVisitProviderList, updateProviderTextVisitStatus } from 'api/operation/textVisitCase';
import { ETextVisitProviderApproveStatus, ETextVisitProviderListSortBy, TTextVisitProviderItem, TTextVisitProviderListParams } from 'types/textVisitCase';
import dayjs from 'dayjs';
import { PATH } from 'constants/path';

const ProviderTable = () => {
    const navigation = useNavigate();

    // State management
    const [itemList, setItemList] = React.useState<TTextVisitProviderItem[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [sortType, setSortType] = React.useState<'DESC' | 'ASC'>();
    const [sortBy, setSortBy] = React.useState<ETextVisitProviderListSortBy>();

    const pageSize = 10;

    // Filter states
    const [pendingNameFilter, setPendingNameFilter] = React.useState('');
    const [nameFilter, setNameFilter] = React.useState<string>('');

    const [pendingReviewerNameFilter, setPendingReviewerNameFilter] = React.useState('');
    const [reviewerNameFilter, setReviewerNameFilter] = React.useState<string>('');

    const [pendingUidFilter, setPendingUidFilter] = React.useState('');
    const [uidFilter, setUidFilter] = React.useState<string>('');
    const [updatingStatus, setUpdatingStatus] = React.useState(false);

    const [approveStatusFilters, setApproveStatusFilters] = React.useState<ETextVisitProviderApproveStatus[]>([]);
    const [showActionModal, setShowActionModal] = React.useState(false);
    const [currentActionRecord, setCurrentActionRecord] = React.useState<TTextVisitProviderItem>();
    const currentAction = React.useRef<ETextVisitProviderApproveStatus>();
    let modalTitle = '';
    let modalContent = '';
    let okText = '';
    let okButtonType = 'primary';
    if (currentAction.current === ETextVisitProviderApproveStatus.APPROVED) {
        modalTitle = 'Confirm text visit approval';
        modalContent = 'Approving will allow this provider to start accepting text visit immediately. Are you sure you want to approve?';
        okText = 'Approve';
    }
    if (currentAction.current === ETextVisitProviderApproveStatus.REJECT) {
        modalTitle = 'Reject text visit';
        modalContent = 'This will reject the provider’s request to join text visits. They will not be able to receive text visit unless they reapply and get approved. Are you sure you want to continue?';
        okText = 'Reject';
        okButtonType = 'danger';
    }
    if (currentAction.current === ETextVisitProviderApproveStatus.APPROVED && currentActionRecord?.status === ETextVisitProviderApproveStatus.PAUSE) {
        modalTitle = 'Resume text visit';
        modalContent = 'Resuming will allow this provider to start receiving text visit cases immediately. Are you sure you want to resume?';
        okText = 'Resume';
    }
    if (currentAction.current === ETextVisitProviderApproveStatus.PAUSE) {
        modalTitle = 'Pause text visit';
        modalContent = 'Pausing will stop this provider from accepting new text visit. They are still required to complete all in-progress cases.';
        okText = 'Pause';
        okButtonType = 'danger';
    }

    // Data fetching function
    const doFetch = async ({
        name,
        reviewBy,
        status,
        uidPublic,
        sortType: sortTypeParam,
        sortBy: sortByParam,
        size,
        current,
    }: TTextVisitProviderListParams) => {
        setLoading(true);

        try {
            const params: Partial<TTextVisitProviderListParams> = {};
            if (name) {
                params.name = name;
            }
            if (reviewBy) {
                params.reviewBy = reviewBy;
            }
            if (status) {
                params.status = status;
            }
            if (uidPublic) {
                params.uidPublic = uidPublic;
            }
            if (sortType) {
                params.sortType = sortTypeParam;
            }
            if (sortBy) {
                params.sortBy = sortByParam;
            }
            if (size) {
                params.size = size;
            }
            if (current) {
                params.current = current;
            }

            // 调用API
            const res = await getTextVisitProviderList(params as TTextVisitProviderListParams);

            if (!res.error && res.data.data) {
                // 直接使用API返回的数据
                setItemList(res.data.data.list);
                setTotal(res.data.data.totalRecords);
            } else {
                setItemList([]);
                setTotal(0);
            }
        } catch (error) {
            // Error fetching cases
            message.error('Failed to fetch cases data');
            setItemList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    // Table change handler
    const onTableChange: TableProps<TTextVisitProviderItem>['onChange'] = (
        pagination,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TTextVisitProviderItem>,
    ) => {
        const pageInx = pagination?.current || 1;
        setPage(pageInx);

        if (sorter && sorter.columnKey) {
            setSortType(sorter.order === 'ascend' ? 'ASC' : 'DESC');
            setSortBy(sorter.columnKey as ETextVisitProviderListSortBy);
        }

        // Handle status filters
        if (filters.status && Array.isArray(filters.status)) {
            setApproveStatusFilters(filters.status as ETextVisitProviderApproveStatus[]);
        } else {
            setApproveStatusFilters([]);
        }
    };

    // Handle view case
    const handleViewCase = (record: TTextVisitProviderItem) => {
        const url = `${PATH.OPERATION}/${PATH.TEXT_VISIT_PROVIDER_CASE}?pid=${record.providerId}&name=${encodeURIComponent(`${record.firstName} ${record.lastName}`)}&status=${record.status}`;
        window.open(url, '_blank');
        // navigation(url);
    };

    // Check if there are any active filters
    const hasActiveFilters = React.useMemo(() => {
        return (
            (approveStatusFilters && approveStatusFilters.length > 0) ||
            reviewerNameFilter !== '' ||
            uidFilter !== '' || nameFilter !== ''
        );
    }, [nameFilter, reviewerNameFilter, uidFilter, approveStatusFilters]);

    const updateStatus = async (record: TTextVisitProviderItem, status: ETextVisitProviderApproveStatus) => {
        setUpdatingStatus(true);
        const res = await updateProviderTextVisitStatus(record.providerId, status);
        setUpdatingStatus(false);
        if (!res.error && res.data.data) {
            let successMessage = '';
            if (status === ETextVisitProviderApproveStatus.APPROVED) {
                successMessage = 'Provider approved for text visits.';
            }
            if (status === ETextVisitProviderApproveStatus.REJECT) {
                successMessage = 'Provider request rejected.';
            }

            if (record.status === ETextVisitProviderApproveStatus.PAUSE && status === ETextVisitProviderApproveStatus.APPROVED) {
                successMessage = 'Providers resumed to receive new cases.';
            }
            if (status === ETextVisitProviderApproveStatus.PAUSE) {
                successMessage = 'Provider is paused from receiving new cases.';
            }
            message.success(successMessage);
            setUpdatingStatus(false);
            setShowActionModal(false);
            doFetch({
                name: nameFilter,
                reviewBy: reviewerNameFilter,
                status: approveStatusFilters,
                uidPublic: uidFilter,
                sortType,
                sortBy,
                size: pageSize,
                current: page,
            });
        } else {
            message.error('Failed to update status');
        }
    };

    const handleApprove = (record: TTextVisitProviderItem) => {
        setCurrentActionRecord(record);
        currentAction.current = ETextVisitProviderApproveStatus.APPROVED;
        setShowActionModal(true);
    };
    const handleReject = (record: TTextVisitProviderItem) => {
        setCurrentActionRecord(record);
        currentAction.current = ETextVisitProviderApproveStatus.REJECT;
        setShowActionModal(true);
    };
    const handleResume = (record: TTextVisitProviderItem) => {
        setCurrentActionRecord(record);
        currentAction.current = ETextVisitProviderApproveStatus.APPROVED;
        setShowActionModal(true);
    };

    const handlePause = (record: TTextVisitProviderItem) => {
        setCurrentActionRecord(record);
        currentAction.current = ETextVisitProviderApproveStatus.PAUSE;
        setShowActionModal(true);
    };

    // Filter groups configuration

    // Initial data fetch
    React.useEffect(() => {
        doFetch({});
    }, []);

    // Auto-fetch when filter panel filters change
    React.useEffect(() => {
        doFetch({
            name: nameFilter,
            reviewBy: reviewerNameFilter,
            status: approveStatusFilters,
            uidPublic: uidFilter,
            sortType,
            sortBy,
            size: pageSize,
            current: page,
        });
    }, [nameFilter, reviewerNameFilter, uidFilter, approveStatusFilters, hasActiveFilters, page, pageSize, sortType, sortBy]);

    const columns: ColumnsType<TTextVisitProviderItem> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            width: '246px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div
                    style={{ padding: 16 }}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Input
                        placeholder="Search patient name"
                        value={pendingNameFilter}
                        onChange={(e) => {
                            setPendingNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setPendingNameFilter(pendingNameFilter);
                            setNameFilter(pendingNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPendingNameFilter(pendingNameFilter);
                                setNameFilter(pendingNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1677ff' : undefined }}
                />
            ),
            render: (text: string, record: TTextVisitProviderItem) => (
                <div className={s.patientCell}>
                    <div className={s.patientName}>
                        {`${record.firstName} ${record.lastName}`.trim()}
                    </div>
                </div>
            ),
        },
        {
            title: 'UID',
            dataIndex: 'uid',
            key: 'uid',
            width: '120px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div
                    style={{ padding: 16 }}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Input
                        placeholder="Search case ID"
                        value={pendingUidFilter}
                        onChange={(e) => {
                            setPendingUidFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setUidFilter(pendingUidFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setUidFilter(pendingUidFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingUidFilter('');
                                setUidFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1677ff' : undefined }}
                />
            ),
            render: (text: string) => <span className={s.caseId}>{text}</span>,
        },
        {
            title: 'APPROVAL STATUS',
            dataIndex: 'status',
            key: 'status',
            width: '196px',
            filters: [
                { text: 'Pending approval', value: ETextVisitProviderApproveStatus.PENDING },
                { text: 'Approved', value: ETextVisitProviderApproveStatus.APPROVED },
                { text: 'Closed', value: ETextVisitProviderApproveStatus.PAUSE },
                { text: 'Rejected', value: ETextVisitProviderApproveStatus.REJECT },
            ],
            render: (status: string) => {
                const statusMap: Record<string, string> = {
                    [ETextVisitProviderApproveStatus.PENDING]: 'Pending approval',
                    [ETextVisitProviderApproveStatus.APPROVED]: 'Approved',
                    [ETextVisitProviderApproveStatus.PAUSE]: 'Paused',
                    [ETextVisitProviderApproveStatus.REJECT]: 'Rejected',
                };
                const displayStatus = statusMap[status] || status;
                return (
                    <span
                        className={`${s.statusBadge} ${s[displayStatus.toLowerCase().replace(/\s+/g, '')]}`}
                    >
                        {displayStatus}
                    </span>
                );
            },
        },
        {
            title: 'IN-PROGRESS CASES',
            dataIndex: 'inProgressCaseQty',
            key: ETextVisitProviderListSortBy.IN_PROGRESS_CASES,
            sortDirections: ['ascend', 'descend'],
            width: '200px',
            sorter: true,
            render: (count: number) => {
                return (
                    <div>{count}</div>
                );
            },
        },
        {
            title: 'ACTIVE CASES',
            dataIndex: 'activeCaseQty',
            key: ETextVisitProviderListSortBy.ACTIVE_CASES,
            sortDirections: ['ascend', 'descend'],
            width: '140px',
            sorter: true,
            render: (count: number) => {
                return (
                    <div>{count}</div>
                );
            },
        },
        {
            title: 'CLOSED CASES',
            dataIndex: 'closedCaseQty',
            key: ETextVisitProviderListSortBy.CLOSE_CASES,
            sortDirections: ['ascend', 'descend'],
            width: '160px',
            sorter: true,
            render: (count: number) => {
                return (
                    <div>{count}</div>
                );
            },
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: '160px',
            sortDirections: ['ascend', 'descend'],
            sorter: true,
            render: (time: string) => {
                return (
                    <div>{time ? dayjs(time).format('MM/DD/YYYY') : '-'}</div>
                );
            },
        },
        {
            title: 'REVIEWED BY',
            dataIndex: 'reviewUserId',
            key: 'reviewUserId',
            width: '246px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div
                    style={{ padding: 16 }}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Input
                        placeholder="Search patient name"
                        value={pendingReviewerNameFilter}
                        onChange={(e) => {
                            setPendingReviewerNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setReviewerNameFilter(pendingReviewerNameFilter);
                            setPendingReviewerNameFilter(pendingReviewerNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setReviewerNameFilter(pendingReviewerNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingReviewerNameFilter('');
                                setReviewerNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1677ff' : undefined }}
                />
            ),
            render: (text: string, record: TTextVisitProviderItem) => (
                <div className={s.patientCell}>
                    <div className={s.patientName}>
                        {`${record.reviewUserFirstName} ${record.reviewUserLastName}`.trim()}
                    </div>
                </div>
            ),
        },

        {
            title: '',
            width: '160px',
            fixed: 'right',
            render: (record: TTextVisitProviderItem) => {
                return (
                    <div className={s.actionWrap}>
                        <span
                            onClick={() => handleViewCase(record)}
                            className={s.link}
                        >
                            View cases
                        </span>
                    </div>
                );
            },
        },
        {
            title: '',
            width: '50px',
            fixed: 'right',
            render: (record: TTextVisitProviderItem) => {
                return (
                    <div className={s.actionWrap}>
                        {
                            record.status === ETextVisitProviderApproveStatus.PENDING && (
                                <Popover
                                    content={(
                                        <div className={s.popMenu}>
                                            <div
                                                className={s.item}
                                                onClick={() => {
                                                    handleApprove(record);
                                                }}
                                            >Approve
                                            </div>
                                            <div
                                                className={s.item}
                                                onClick={() => {
                                                    handleReject(record);
                                                }}
                                            >Reject
                                            </div>
                                        </div>
                                    )}
                                    title={null}
                                    trigger="hover"
                                >
                                    <span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 5L12 5.01M12 12L12 12.01M12 19L12 19.01M12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6ZM12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13ZM12 20C11.4477 20 11 19.5523 11 19C11 18.4477 11.4477 18 12 18C12.5523 18 13 18.4477 13 19C13 19.5523 12.5523 20 12 20Z" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </Popover>
                            )
                        }
                        {
                            record.status === ETextVisitProviderApproveStatus.PAUSE && (
                                <Popover
                                    content={(
                                        <div className={s.popMenu}>
                                            <div
                                                className={s.item}
                                                onClick={() => {
                                                    handleResume(record);
                                                }}
                                            >Resume
                                            </div>
                                        </div>
                                    )}
                                    title={null}
                                    trigger="hover"
                                >
                                    <span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 5L12 5.01M12 12L12 12.01M12 19L12 19.01M12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6ZM12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13ZM12 20C11.4477 20 11 19.5523 11 19C11 18.4477 11.4477 18 12 18C12.5523 18 13 18.4477 13 19C13 19.5523 12.5523 20 12 20Z" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </Popover>
                            )
                        }
                        {
                            record.status === ETextVisitProviderApproveStatus.REJECT && (
                                <Popover
                                    content={(
                                        <div className={s.popMenu}>
                                            <div
                                                className={s.item}
                                                onClick={() => {
                                                    handleApprove(record);
                                                }}
                                            >Approve
                                            </div>
                                        </div>
                                    )}
                                    title={null}
                                    trigger="hover"
                                >
                                    <span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 5L12 5.01M12 12L12 12.01M12 19L12 19.01M12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6ZM12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13ZM12 20C11.4477 20 11 19.5523 11 19C11 18.4477 11.4477 18 12 18C12.5523 18 13 18.4477 13 19C13 19.5523 12.5523 20 12 20Z" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </Popover>
                            )
                        }
                        {
                            record.status === ETextVisitProviderApproveStatus.APPROVED && (
                                <Popover
                                    content={(
                                        <div className={s.popMenu}>
                                            <div
                                                className={s.item}
                                                onClick={() => {
                                                    handlePause(record);
                                                }}
                                            >Pause
                                            </div>
                                        </div>
                                    )}
                                    title={null}
                                    trigger="hover"
                                >
                                    <span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 5L12 5.01M12 12L12 12.01M12 19L12 19.01M12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6ZM12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13ZM12 20C11.4477 20 11 19.5523 11 19C11 18.4477 11.4477 18 12 18C12.5523 18 13 18.4477 13 19C13 19.5523 12.5523 20 12 20Z" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </Popover>
                            )
                        }
                    </div>
                );
            },
        },
    ].filter(Boolean) as ColumnsType<TTextVisitProviderItem>;

    return (
        <div className={s.wrap}>
            <CommonTable
                bordered
                rowKey="id"
                pagination={{
                    total,
                    current: page,
                    pageSize,
                    showSizeChanger: false,
                }}
                scroll={{ x: 'max-content' }}
                loading={loading}
                columns={columns}
                onChange={onTableChange}
                data={itemList || []}
            />
            <Modal
                title={modalTitle}
                open={showActionModal}
                onCancel={() => setShowActionModal(false)}
                onOk={() => {
                    if (currentAction.current && currentActionRecord) {
                        updateStatus(currentActionRecord, currentAction.current);
                    }
                }}
                width={550}
                okButtonProps={{
                    danger: okButtonType === 'danger',
                    type: 'primary',
                    loading: updatingStatus,
                }}
                okText={okText}
            >
                <div className={s.modalContent}>
                    {modalContent}
                </div>
            </Modal>
        </div>
    );
};

export default ProviderTable;
